import _extends from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties";
import _asyncToGenerator from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator";
var _excluded = ["name", "defaultValue", "loading", "removeInvalidValue"];
import React from "react";
var __jsx = React.createElement;
import _regeneratorRuntime from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/regenerator";
import { LinearProgress } from '@material-ui/core';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { TagAutocompleteChip } from 'components/common/TagAutocompleteChip';
import { TagAutocompleteOption } from 'components/common/TagAutocompleteOption';
import { TagAutocomplete } from 'components/common/material-ui/custom-components';
import { useFieldError } from 'libs/content/hooks';
import TagInputTextField from './TagInputTextField';
import { useTagOptions } from './hooks';
import { filterOptions, freeSoloFilterOptions, getOptionLabel, hasOptionBeenFilled } from './utils';

var ControlledTagInput = function ControlledTagInput(_ref) {
  var labelId = _ref.labelId,
      options = _ref.options,
      name = _ref.name,
      multipleTag = _ref.multipleTag,
      _ref$freeSolo = _ref.freeSolo,
      freeSolo = _ref$freeSolo === void 0 ? false : _ref$freeSolo,
      createOption = _ref.createOption,
      values = _ref.values,
      onChange = _ref.onChange,
      removeNotFoundValues = _ref.removeNotFoundValues,
      formatLabel = _ref.formatLabel,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$showTitle = _ref.showTitle,
      showTitle = _ref$showTitle === void 0 ? false : _ref$showTitle;
  var intl = useIntl();
  var inputRef = useRef(null);
  var errorMessage = useFieldError(name);

  var _useTagOptions = useTagOptions(options, values, {
    createOption: createOption,
    allowMultipleSelection: multipleTag,
    removeNotFoundValues: removeNotFoundValues
  }),
      availableTagOptions = _useTagOptions.availableTagOptions,
      selectedValues = _useTagOptions.selectedValues,
      selectedTagOptions = _useTagOptions.selectedTagOptions,
      newTagOptionsToValues = _useTagOptions.newTagOptionsToValues;

  var doBlur = useCallback(function () {
    // Avoid users to enter extra text if autocomplete is filled and doesn't accept multiple tags
    if (!multipleTag && values.length > 0) {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur();
    }
  }, [multipleTag, values]);
  useEffect(doBlur, [doBlur]);

  var filter = function filter(_options, _state) {
    return freeSolo && (values.length === 0 || multipleTag) ? freeSoloFilterOptions(_options, _state, intl, selectedTagOptions) : filterOptions(_options, _state);
  };

  var displayedOptions = multipleTag || selectedValues.length === 0 ? availableTagOptions.filter(function (_ref2) {
    var id = _ref2.id;
    return !selectedValues.includes(id);
  }) : [];
  return __jsx(TagAutocomplete, {
    onFocus: doBlur,
    multipleTag: multipleTag,
    multiple: true,
    title: showTitle ? selectedTagOptions.map(function (option) {
      return option.title;
    }).join(', ') : undefined,
    options: displayedOptions,
    freeSolo: freeSolo,
    disableClearable: true,
    filterSelectedOptions: true,
    forcePopupIcon: false,
    disabled: disabled,
    noOptionsText: intl.formatMessage({
      id: "tagAutocomplete.noOptionsText.".concat(multipleTag ? 'default' : 'filled')
    }) // do not work combined with freeSolo
    ,
    getOptionLabel: getOptionLabel,
    getOptionSelected: function getOptionSelected(option, selectedValue) {
      return option.id === selectedValue.id;
    },
    getOptionDisabled: hasOptionBeenFilled,
    value: selectedTagOptions,
    onChange: /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_, _values) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.t0 = onChange;
                _context.next = 3;
                return newTagOptionsToValues(_values);

              case 3:
                _context.t1 = _context.sent;
                return _context.abrupt("return", (0, _context.t0)(_context.t1));

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2) {
        return _ref3.apply(this, arguments);
      };
    }(),
    filterOptions: filter,
    renderOption: function renderOption(option, state) {
      return __jsx(TagAutocompleteOption, {
        label: option.title,
        state: state,
        isMultiple: multipleTag
      });
    },
    renderTags: function renderTags(_values, getTagProps) {
      return _values.map(function (option, index) {
        return __jsx(TagAutocompleteChip, {
          key: option.id,
          label: formatLabel === undefined ? option.title : formatLabel(option.title),
          title: option.title,
          getTagProps: getTagProps,
          index: index,
          isMultiple: multipleTag,
          deleteIcon: true
        });
      });
    },
    renderInput: function renderInput(params) {
      return __jsx(TagInputTextField, {
        params: params,
        label: intl.formatMessage({
          id: labelId
        }),
        errorMessage: errorMessage,
        inputRef: inputRef
      });
    }
  });
};

var TagInput = function TagInput(_ref4) {
  var name = _ref4.name,
      defaultValue = _ref4.defaultValue,
      loading = _ref4.loading,
      _ref4$removeInvalidVa = _ref4.removeInvalidValue,
      removeInvalidValue = _ref4$removeInvalidVa === void 0 ? false : _ref4$removeInvalidVa,
      props = _objectWithoutProperties(_ref4, _excluded);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  var _useFormContext = useFormContext(),
      control = _useFormContext.control,
      setValue = _useFormContext.setValue;

  var removeNotFoundValues = function removeNotFoundValues(values, filterFunction) {
    if (removeInvalidValue === true) {
      setValue(name, values.filter(filterFunction));
    }
  };

  return __jsx(Controller, {
    control: control,
    name: name,
    defaultValue: defaultValue,
    render: function render(_ref5) {
      var _ref5$field = _ref5.field,
          onChange = _ref5$field.onChange,
          value = _ref5$field.value;
      return __jsx(React.Fragment, null, loading ? __jsx(LinearProgress, null) : __jsx(ControlledTagInput, _extends({
        name: name,
        values: value !== null && value !== void 0 ? value : [],
        onChange: onChange,
        removeNotFoundValues: removeNotFoundValues
      }, props)));
    }
  });
};

export default TagInput;